import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ReceiptText, Download, Loader2, X } from 'lucide-react';
import { storeApi } from './storeApi';
import StoreLayout from './StoreLayout';

export default function StoreInvoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    setLoading(true);
    storeApi.get('/store/invoices')
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!fromDate && !toDate) return rows;
    const fromTs = fromDate ? new Date(fromDate + 'T00:00:00').getTime() : null;
    const toTs = toDate ? new Date(toDate + 'T23:59:59').getTime() : null;
    return rows.filter((inv) => {
      const raw = inv.date || inv.created_at;
      if (!raw) return true;
      const ts = new Date(raw).getTime();
      if (Number.isNaN(ts)) return true;
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;
      return true;
    });
  }, [rows, fromDate, toDate]);

  async function download(inv) {
    setDownloadingId(inv.id);
    try {
      const res = await storeApi.get(`/store/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number || inv.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${inv.invoice_number}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Download failed';
      toast.error(typeof msg === 'string' ? msg : 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  }

  function clearDateFilter() { setFromDate(''); setToDate(''); }

  return (
    <StoreLayout>
      <h1 className="store-headline text-3xl mb-4">My Invoices</h1>

      <div className="store-card p-3 mb-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-slate-500 uppercase">Filter by date</span>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                 max={toDate || undefined}
                 className="store-input !py-1 !px-2 text-sm w-auto"
                 data-testid="invoices-from-date" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                 min={fromDate || undefined}
                 className="store-input !py-1 !px-2 text-sm w-auto"
                 data-testid="invoices-to-date" />
        </div>
        {(fromDate || toDate) && (
          <button onClick={clearDateFilter}
                  className="text-xs text-orange-700 hover:underline flex items-center gap-1"
                  data-testid="invoices-clear-date">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">
          Showing {filtered.length} of {rows.length} invoices
        </span>
      </div>

      {loading ? (
        <div className="text-center py-20 text-orange-700">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="store-card p-12 text-center text-slate-400">
          <ReceiptText className="w-10 h-10 mx-auto mb-3 text-orange-700" />
          No invoices yet — they&apos;ll appear here once your dispatches are billed.
        </div>
      ) : filtered.length === 0 ? (
        <div className="store-card p-12 text-center text-slate-400">
          No invoices in the selected date range.
        </div>
      ) : (
        <div className="store-card overflow-hidden" data-testid="store-invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-orange-50">
              <tr className="text-orange-900 text-xs uppercase">
                <th className="text-left px-4 py-3">Invoice #</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-center px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3 w-24">Download</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-200 hover:bg-orange-50/40"
                    data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-orange-900">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-slate-600">{(inv.date || inv.created_at || '').split('T')[0]}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{inv.items?.length || 0}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{Number(inv.total_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-center text-xs uppercase font-semibold">{inv.status || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => download(inv)}
                            disabled={downloadingId === inv.id}
                            className="text-orange-700 hover:bg-orange-50 px-3 py-1.5 rounded-md border border-orange-200 inline-flex items-center gap-1 text-xs font-semibold disabled:opacity-50"
                            data-testid={`download-invoice-${inv.id}`}>
                      {downloadingId === inv.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Download className="w-3 h-3" />}
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </StoreLayout>
  );
}
